import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroImage = styled.img`
  width: 100%;
  display: block;
  max-height: 520px;
  object-fit: cover;
  object-position: center top;
`;

const ScriptureSection = styled.section`
  background: #f8f4ef;
  padding: 70px 20px;
  text-align: center;
`;

const ScriptureInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const ScriptureVerse = styled.blockquote`
  font-size: 17px;
  line-height: 2;
  color: #444;
  font-style: italic;
  margin: 0 0 10px 0;
  padding: 0;
`;

const ScriptureRef = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #2c5aa0;
  margin-bottom: 44px;
`;

const MinistryIntro = styled.p`
  font-size: 16px;
  line-height: 2;
  color: #555;
  margin-bottom: 16px;
  word-break: keep-all;
`;

const OneChurchSection = styled.section`
  background: linear-gradient(135deg, #1e3a8a 0%, #2c5aa0 100%);
  padding: 70px 20px;
  text-align: center;
  color: #fff;
`;

const OneChurchTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 18px;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const OneChurchSubtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.9;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const DirectionSection = styled.section`
  background: #fff;
  padding: 70px 20px;
`;

const DirectionInner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  color: #2c5aa0;
  text-align: center;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const DirectionFlow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const DirectionStep = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1a2a4a;
  background: #e8f0fe;
  border-radius: 30px;
  padding: 10px 22px;
`;

const DirectionArrow = styled.span`
  font-size: 22px;
  color: #2c5aa0;
`;

const DirectionDesc = styled.p`
  font-size: 15px;
  line-height: 2;
  color: #555;
  word-break: keep-all;
`;

const FlowSection = styled.section`
  background: #f8f9fa;
  padding: 70px 20px;
`;

const FlowInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const FlowImage = styled.img`
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
`;

const ProgramsSection = styled.section`
  background: #fff;
  padding: 80px 20px;
`;

const ProgramsInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const ProgramCard = styled.div<{ $reverse?: boolean }>`
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 60px;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProgramImageWrap = styled.div<{ $reverse?: boolean }>`
  order: ${({ $reverse }) => ($reverse ? 1 : 0)};

  @media (max-width: 768px) {
    order: 0;
  }
`;

const ProgramImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  min-height: 320px;
  display: block;

  @media (max-width: 768px) {
    min-height: 220px;
  }
`;

const ProgramContent = styled.div<{ $reverse?: boolean }>`
  padding: 50px 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  order: ${({ $reverse }) => ($reverse ? 0 : 1)};

  @media (max-width: 768px) {
    padding: 28px 24px;
    order: 1;
  }
`;

const ProgramBadge = styled.span<{ $color: string }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  background: ${({ $color }) => $color};
  color: #fff;
  border-radius: 20px;
  padding: 5px 14px;
  margin-bottom: 16px;
  width: fit-content;
`;

const ProgramTitle = styled.h3`
  font-size: 26px;
  font-weight: 800;
  color: #1a2a4a;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const ProgramSubtitle = styled.p`
  font-size: 14px;
  color: #999;
  margin-bottom: 20px;
  font-weight: 600;
`;

const ProgramDescription = styled.p`
  font-size: 15px;
  line-height: 1.9;
  color: #555;
  margin-bottom: 12px;
  word-break: keep-all;
`;

const ActivityFlow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 16px 0;
`;

const ActivityBadge = styled.span`
  font-size: 13px;
  background: #eef2ff;
  color: #2c5aa0;
  border-radius: 6px;
  padding: 4px 12px;
  font-weight: 600;
`;

const ActivityArrow = styled.span`
  font-size: 13px;
  color: #bbb;
`;

const ProgramGoal = styled.div`
  margin-top: 20px;
  padding: 16px 20px;
  background: #f0f4ff;
  border-left: 4px solid #2c5aa0;
  border-radius: 0 8px 8px 0;
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  word-break: keep-all;

  strong {
    color: #2c5aa0;
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    letter-spacing: 0.5px;
    font-weight: 700;
  }
`;

const FullWidthImageSection = styled.section`
  line-height: 0;
`;

const FullWidthImg = styled.img`
  width: 100%;
  display: block;
`;

const Youth: React.FC = () => {
  return (
    <PageContainer>

      {/* Hero Banner */}
      <HeroImage src="/youth-hero.png" alt="말씀으로 자라는 Next Generation" />

      {/* Scripture */}
      <ScriptureSection>
        <ScriptureInner>
          <ScriptureVerse>
            "모든 성경은 하나님의 감동으로 된 것으로 교훈과 책망과 바르게 함과 의로 교육하기에 유익하니
            이는 하나님의 사람으로 온전하게 하며 모든 선한 일을 행할 능력을 갖추게 하려 함이라."
          </ScriptureVerse>
          <ScriptureRef>디모데후서 3:16–17</ScriptureRef>
          <MinistryIntro>
            IJBC 다음세대 사역은 우리 자녀들이 어려서부터 하나님의 말씀을 알고, 하나님을 사랑하며,
            하나님의 뜻을 따라 살아가는 믿음의 사람으로 성장하도록 돕는 것을 목표로 합니다.
          </MinistryIntro>
          <MinistryIntro>
            우리는 다음세대의 신앙교육이 주일 한 시간의 예배로 끝나지 않는다고 믿습니다.
            주일예배에서 말씀을 배우고, 일상에서 말씀을 묵상하며, 가정에서 부모와 함께 믿음을 살아내는 것,
            이것이 IJBC가 추구하는 다음세대 신앙교육입니다.
          </MinistryIntro>
        </ScriptureInner>
      </ScriptureSection>

      {/* One Church, One Word, One Faith */}
      <OneChurchSection>
        <OneChurchTitle>One Church, One Word, One Faith</OneChurchTitle>
        <OneChurchSubtitle>부모와 자녀가 함께 말씀을 듣고, 함께 믿고, 함께 살아갑니다.</OneChurchSubtitle>
      </OneChurchSection>

      {/* Direction */}
      <DirectionSection>
        <DirectionInner>
          <SectionTitle>우리의 방향</SectionTitle>
          <DirectionFlow>
            <DirectionStep>말씀을 배우고</DirectionStep>
            <DirectionArrow>→</DirectionArrow>
            <DirectionStep>말씀을 믿고</DirectionStep>
            <DirectionArrow>→</DirectionArrow>
            <DirectionStep>말씀을 살아가는 다음세대</DirectionStep>
          </DirectionFlow>
          <DirectionDesc>
            아이들이 성경 이야기를 많이 아는 것에만 머물지 않고, 하나님은 어떤 분이신지 알고,
            예수 그리스도를 자신의 구주로 믿으며, 말씀을 통해 하나님의 뜻을 분별하고,
            그 말씀에 순종하며 살아가는 하나님의 자녀로 성장하도록 돕습니다.
          </DirectionDesc>
        </DirectionInner>
      </DirectionSection>

      {/* Flow Diagram */}
      <FlowSection>
        <FlowInner>
          <SectionTitle>다음세대 사역 구조</SectionTitle>
          <FlowImage src="/youth-flow.png" alt="Church → Daily Life → Home 다음세대 사역 흐름도" />
        </FlowInner>
      </FlowSection>

      {/* Programs */}
      <ProgramsSection>
        <ProgramsInner>
          <SectionTitle>사역 프로그램</SectionTitle>

          {/* Youth Group 1 */}
          <ProgramCard>
            <ProgramImageWrap>
              <ProgramImg src="/youth-kids-worship.png" alt="KIDS 말씀예배" />
            </ProgramImageWrap>
            <ProgramContent $reverse>
              <ProgramBadge $color="#4a90d9">YOUTH GROUP 1</ProgramBadge>
              <ProgramTitle>어린이 말씀예배</ProgramTitle>
              <ProgramSubtitle>KM · 10세 미만 | 주일 오전 11:00</ProgramSubtitle>
              <ProgramDescription>
                어린이들이 자신의 눈높이에 맞게 하나님의 말씀을 즐겁게 배우고 경험하는 예배입니다.
                율동찬양, 성경구연, 드라마, 영상, 그림과 놀이 등 다양한 활동을 통해 성경의 말씀을 이해하고
                하나님을 사랑하도록 돕습니다.
              </ProgramDescription>
              <ProgramDescription>
                모든 활동의 중심에는 하나님의 말씀이 있습니다.
              </ProgramDescription>
              <ActivityFlow>
                <ActivityBadge>영상예배</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>성경읽기</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>말씀 이해 활동</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>그리기·놀이·체험</ActivityBadge>
              </ActivityFlow>
              <ProgramGoal>
                <strong>교육목표</strong>
                "말씀을 통해 하나님을 알고 하나님을 사랑하는 어린이"
              </ProgramGoal>
            </ProgramContent>
          </ProgramCard>

          {/* Youth Group 2 */}
          <ProgramCard>
            <ProgramImageWrap $reverse>
              <ProgramImg
                src="/youth-hero.png"
                alt="영어 학생예배"
                style={{ objectPosition: 'center 30%' }}
              />
            </ProgramImageWrap>
            <ProgramContent>
              <ProgramBadge $color="#2c5aa0">YOUTH GROUP 2</ProgramBadge>
              <ProgramTitle>영어 학생예배</ProgramTitle>
              <ProgramSubtitle>EM · 10세 이상 | 주일 오전 11:00</ProgramSubtitle>
              <ProgramDescription>
                10세 이상의 학생들을 위한 영어예배입니다. 성인예배와 연결된 말씀과 주제를 학생들의
                연령과 언어 수준에 맞추어 영어로 전함으로써, 온 교회가 같은 말씀과 신앙의 방향을 공유합니다.
              </ProgramDescription>
              <ProgramDescription>
                예배 후에도 부모와 자녀가 함께 말씀에 대해 이야기하고, 한 주간 동일한 말씀을
                삶에 적용할 수 있도록 돕습니다.
              </ProgramDescription>
              <ActivityFlow>
                <ActivityBadge>말씀</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>결단찬양과 기도</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>삶의 적용</ActivityBadge>
                <ActivityArrow>→</ActivityArrow>
                <ActivityBadge>QT Check-up</ActivityBadge>
              </ActivityFlow>
              <ProgramGoal>
                <strong>교육목표</strong>
                "말씀을 스스로 읽고 믿으며 삶에 적용하는 학생"
              </ProgramGoal>
            </ProgramContent>
          </ProgramCard>

        </ProgramsInner>
      </ProgramsSection>

      {/* Youth Zoom Meeting */}
      <FullWidthImageSection>
        <FullWidthImg src="/youth-zoom-meeting.png" alt="Youth Zoom Meeting — 주일 말씀을 일상의 말씀으로" />
      </FullWidthImageSection>

      {/* Parents QT */}
      <FullWidthImageSection>
        <FullWidthImg src="/youth-parents-qt.png" alt="Parents QT 부모 말씀동행" />
      </FullWidthImageSection>

    </PageContainer>
  );
};

export default Youth;
